import type {
  ExecutionMode,
  Stakes,
  TargetQuality,
} from '../domain/types'

// Keep production routing and policy evaluation on the same deterministic
// prompt-context inference path.
export type ContextInferenceSource =
  | 'explicit-prompt-signal'
  | 'policy-default'

export type InferredContextField<T> = {
  value: T
  source: ContextInferenceSource
  signalIds: string[]
}

export type InferredWorkItemContext = {
  executionMode: InferredContextField<ExecutionMode>
  stakes: InferredContextField<Stakes>
  targetQuality: InferredContextField<TargetQuality>
}

export const FIXED_DEFAULT_CONTEXT = {
  executionMode: 'chat',
  stakes: 'normal',
  targetQuality: 'usable',
} as const

type SignalRule = {
  id: string
  pattern: RegExp
}

function matchingSignalIds(
  prompt: string,
  rules: SignalRule[],
): string[] {
  return rules
    .filter(({ pattern }) => pattern.test(prompt))
    .map(({ id }) => id)
}

const AGENT_RULES: SignalRule[] = [
  {
    id: 'tool-call',
    pattern:
      /\b(?:call|use)\s+(?:the\s+)?[\w-]+\s+tool\b/i,
  },
  {
    id: 'reproduce-fix-prove',
    pattern:
      /\b(?:reproduce|establish a reproduction)\b[\s\S]*\b(?:implement|fix)\b[\s\S]*\b(?:prove|verify|tests?)\b/i,
  },
  {
    id: 'long-horizon-delivery',
    pattern:
      /\b(?:research[\s,]+plan[\s,]+implement|coordinate the dependent phases|validate[\s,]+and release)\b/i,
  },
  {
    id: 'operational-plan',
    pattern:
      /\bplan\b[\s\S]*\b(?:affected files?|validation commands?|deployment verification)\b/i,
  },
  {
    id: 'diagnose-and-verify',
    pattern:
      /\bdiagnos(?:e|is)\b[\s\S]*\b(?:verify the fix|concurrent load)\b/i,
  },
  {
    id: 'production-investigation',
    pattern:
      /\b(?:find why|investigate why)\b[\s\S]*\bproduction\b/i,
  },
  {
    id: 'implementation-with-release-check',
    pattern:
      /\bimplement\b[\s\S]*\b(?:regression test|deployment verification checklist)\b/i,
  },
]

const CODE_EDIT_RULES: SignalRule[] = [
  {
    id: 'named-source-file',
    pattern:
      /(?:^|[\s`])(?:\.{0,2}\/)?[\w-]+\/[\w./-]+\.[a-z0-9]+(?:$|[\s`,:])/i,
  },
  {
    id: 'code-artifact-change',
    pattern:
      /\b(?:add|change|fix|implement|migrate|refactor|rename|update)\b[\s\S]*\b(?:component|middleware|package|worker|api client|unit test|regression test|source file)\b/i,
  },
  {
    id: 'diff-review',
    pattern:
      /\b(?:review|audit)\b[\s\S]*\b(?:diff|middleware|tests?)\b/i,
  },
]

const RESEARCH_RULES: SignalRule[] = [
  {
    id: 'explicit-research',
    pattern:
      /\b(?:research|look up|find sources?|source-backed)\b/i,
  },
  {
    id: 'current-official-facts',
    pattern:
      /\b(?:current|latest)\b[\s\S]*\b(?:official documentation|official sources?|citations?|cited)\b/i,
  },
  {
    id: 'evidence-synthesis',
    pattern:
      /\b(?:reconcile conflicting|audit friendly-licensed benchmark|recommend an architecture|return a cited table)\b/i,
  },
  {
    id: 'architecture-audit',
    pattern:
      /\baudit\b[\s\S]*\b(?:architecture|trust-boundary|trust boundary)\b/i,
  },
]

const HIGH_STAKES_RULES: SignalRule[] = [
  {
    id: 'explicit-high-stakes',
    pattern:
      /\b(?:high[- ]stakes|safety-critical|exploitable|trust-boundary|customer data)\b/i,
  },
  {
    id: 'payment-harm',
    pattern:
      /\b(?:payments?|double-charged|double charged)\b/i,
  },
  {
    id: 'production-sensitive-system',
    pattern:
      /\bproduction\b[\s\S]*\b(?:authentication|authorization|security|payments?|migration|incident|release)\b/i,
  },
  {
    id: 'long-horizon-release',
    pattern:
      /\b(?:multi-system data migration|release a multi-system|rollback gates?)\b/i,
  },
]

const LOW_STAKES_RULES: SignalRule[] = [
  {
    id: 'explicit-low-stakes',
    pattern:
      /\b(?:low[- ]stakes|throwaway|disposable experiment)\b/i,
  },
]

const PRODUCTION_QUALITY_RULES: SignalRule[] = [
  {
    id: 'production-output',
    pattern:
      /\b(?:production|production-ready|release|rollback)\b/i,
  },
  {
    id: 'deployment-verification',
    pattern:
      /\bdeployment\b[\s\S]{0,60}\b(?:verification|checklist)\b/i,
  },
  {
    id: 'hard-verification',
    pattern:
      /\b(?:adversarial tests?|regression tests?|verify the fix under concurrent load)\b/i,
  },
]

const DRAFT_QUALITY_RULES: SignalRule[] = [
  {
    id: 'explicit-draft',
    pattern:
      /\b(?:rough draft|first draft|prototype only|exploratory sketch)\b/i,
  },
]

function inferExecutionMode(
  prompt: string,
): InferredContextField<ExecutionMode> {
  const agentSignals = matchingSignalIds(prompt, AGENT_RULES)
  if (agentSignals.length > 0) {
    return {
      value: 'agent',
      source: 'explicit-prompt-signal',
      signalIds: agentSignals,
    }
  }

  const codeEditSignals = matchingSignalIds(
    prompt,
    CODE_EDIT_RULES,
  )
  if (codeEditSignals.length > 0) {
    return {
      value: 'code-edit',
      source: 'explicit-prompt-signal',
      signalIds: codeEditSignals,
    }
  }

  const researchSignals = matchingSignalIds(
    prompt,
    RESEARCH_RULES,
  )
  if (researchSignals.length > 0) {
    return {
      value: 'research',
      source: 'explicit-prompt-signal',
      signalIds: researchSignals,
    }
  }

  return {
    value: FIXED_DEFAULT_CONTEXT.executionMode,
    source: 'policy-default',
    signalIds: [],
  }
}

function inferStakes(
  prompt: string,
): InferredContextField<Stakes> {
  const highSignals = matchingSignalIds(
    prompt,
    HIGH_STAKES_RULES,
  )
  if (highSignals.length > 0) {
    return {
      value: 'high',
      source: 'explicit-prompt-signal',
      signalIds: highSignals,
    }
  }

  const lowSignals = matchingSignalIds(prompt, LOW_STAKES_RULES)
  if (lowSignals.length > 0) {
    return {
      value: 'low',
      source: 'explicit-prompt-signal',
      signalIds: lowSignals,
    }
  }

  return {
    value: FIXED_DEFAULT_CONTEXT.stakes,
    source: 'policy-default',
    signalIds: [],
  }
}

function inferTargetQuality(
  prompt: string,
): InferredContextField<TargetQuality> {
  const productionSignals = matchingSignalIds(
    prompt,
    PRODUCTION_QUALITY_RULES,
  )
  if (productionSignals.length > 0) {
    return {
      value: 'production',
      source: 'explicit-prompt-signal',
      signalIds: productionSignals,
    }
  }

  const draftSignals = matchingSignalIds(
    prompt,
    DRAFT_QUALITY_RULES,
  )
  if (draftSignals.length > 0) {
    return {
      value: 'draft',
      source: 'explicit-prompt-signal',
      signalIds: draftSignals,
    }
  }

  return {
    value: FIXED_DEFAULT_CONTEXT.targetQuality,
    source: 'policy-default',
    signalIds: [],
  }
}

export function inferWorkItemContext(
  prompt: string,
): InferredWorkItemContext {
  const normalizedPrompt = prompt.trim().replace(/\s+/g, ' ')

  return {
    executionMode: inferExecutionMode(normalizedPrompt),
    stakes: inferStakes(normalizedPrompt),
    targetQuality: inferTargetQuality(normalizedPrompt),
  }
}
